/**
 * HOME BLOCK 03 - 3 Image Grid
 * GSAP ScrollTrigger Animation
 *
 * FEATURES:
 * - Sticky scroll effect using GSAP pin
 * - Desktop only (≥1024px)
 * - Multiple section instances supported
 * - Button fade animation on scroll
 * - Drawer controls
 * - Singleton pattern to prevent multiple initializations
 */

// Use global namespace to prevent duplicate initializations
window.HomeBlock03GSAP = window.HomeBlock03GSAP || (function() {
  'use strict';

  // =============================================
  // CONFIGURATION
  // =============================================

  const CONFIG = {
    BREAKPOINT: 1024,
    HEADER_HEIGHT: 63.27, // Sticky header height (dynamically calculated)
    DEBUG: false, // Set to true for ScrollTrigger markers

    SELECTORS: {
      section: '[data-section-id]',
      grid: '.animation-grid-container',
      largeColumn: '.animation-large-column',
      stickyContent: '.animation-sticky-content',
      smallColumn: '.animation-small-column',
      smallColumnInner: '.callout-column-small-inner',
      triggerButton: '.animation-trigger-button',
      largeButton: '.animation-large-button',

      // Drawer selectors
      drawerTrigger: '[data-drawer-trigger]',
      drawerOne: '[data-drawer-one]',
      drawerOverlay: '[data-drawer-overlay]',
      drawerClose: '[data-drawer-close]',
      subDrawerTrigger: '[data-sub-drawer-trigger]',
      subDrawerClose: '[data-sub-drawer-close]',
      drawerSub: '[data-drawer-sub]'
    }
  };

  // =============================================
  // SCROLL DIRECTION TRACKING (SINGLETON)
  // =============================================

  let lastScrollY = window.scrollY || 0;
  let scrollDirection = 'up'; // Start with 'up' (header visible on load)
  let currentHeaderOffset = CONFIG.HEADER_HEIGHT; // Start with gutter (header visible by default)
  let previousDirection = null; // No previous direction on init
  let scrollListenerInitialized = false;

  function updateScrollDirection() {
    const currentScrollY = window.scrollY;

    if (currentScrollY > lastScrollY) {
      // Scrolling DOWN (going deeper into site) → Header HIDES → Remove gutter
      scrollDirection = 'down';

      // Only process if direction changed
      if (scrollDirection !== previousDirection) {
        log('Scroll direction changed: ' + previousDirection + ' → ' + scrollDirection);
        log('Header HIDING - removing gutter');
        currentHeaderOffset = 0;

        // Refresh ScrollTrigger to update start positions
        if (typeof ScrollTrigger !== 'undefined') {
          ScrollTrigger.refresh();
          log('ScrollTrigger refreshed with offset: ' + currentHeaderOffset + 'px');
        }

        previousDirection = scrollDirection;
      }

    } else if (currentScrollY < lastScrollY) {
      // Scrolling UP (going back up) → Header SHOWS → Apply gutter
      scrollDirection = 'up';

      // Only process if direction changed
      if (scrollDirection !== previousDirection) {
        log('Scroll direction changed: ' + previousDirection + ' → ' + scrollDirection);
        log('Header SHOWING - applying gutter');
        currentHeaderOffset = CONFIG.HEADER_HEIGHT;

        // Refresh ScrollTrigger to update start positions
        if (typeof ScrollTrigger !== 'undefined') {
          ScrollTrigger.refresh();
          log('ScrollTrigger refreshed with offset: ' + currentHeaderOffset + 'px');
        }

        previousDirection = scrollDirection;
      }
    }

    lastScrollY = currentScrollY;
  }

  function initScrollDirectionTracking() {
    // Only initialize once, even if multiple sections exist
    if (!scrollListenerInitialized) {
      window.addEventListener('scroll', updateScrollDirection, { passive: true });
      scrollListenerInitialized = true;
      log('Scroll direction tracking initialized (singleton)');
    }
  }

  // =============================================
  // DEBUG LOGGER
  // =============================================

  function log(message, data) {
    if (CONFIG.DEBUG) {
      if (data !== undefined) {
        console.log('[HomeBlock03-GSAP]', message, data);
      } else {
        console.log('[HomeBlock03-GSAP]', message);
      }
    }
  }

  // =============================================
  // SCROLL ANIMATIONS
  // =============================================

  function initScrollAnimations() {
    log('Initializing scroll animations');

    // Only run on desktop
    if (window.innerWidth < CONFIG.BREAKPOINT) {
      log('Screen width below breakpoint, skipping animations');
      return;
    }

    const sections = document.querySelectorAll(CONFIG.SELECTORS.section);
    log('Found ' + sections.length + ' section(s)');

    sections.forEach(function(section, index) {
      const sectionId = section.dataset.sectionId;
      log('Processing section ' + index + ' (ID: ' + sectionId + ')');
      initSectionAnimation(section, sectionId, index);
    });
  }

  function initSectionAnimation(section, sectionId, index) {
    const grid = section.querySelector(CONFIG.SELECTORS.grid);
    const stickyContent = section.querySelector(CONFIG.SELECTORS.stickyContent);
    const smallColumn = section.querySelector(CONFIG.SELECTORS.smallColumn);
    const smallColumnInner = section.querySelector(CONFIG.SELECTORS.smallColumnInner);
    const triggerButton = section.querySelector(CONFIG.SELECTORS.triggerButton);
    const largeButton = section.querySelector(CONFIG.SELECTORS.largeButton);

    if (!grid || !stickyContent) {
      log('Section ' + index + ': Missing required elements');
      return;
    }

    // Use smallColumnInner for height calculation (same as vanilla JS)
    const heightElement = smallColumnInner || smallColumn;
    if (!heightElement) {
      log('Section ' + index + ': No small column found');
      return;
    }

    // Calculate pin duration based on height difference
    const stickyHeight = stickyContent.offsetHeight;
    const smallHeight = heightElement.offsetHeight;
    const pinDistance = stickyHeight - smallHeight;

    log('Section ' + index + ' heights:', {
      stickyHeight: stickyHeight,
      smallHeight: smallHeight,
      pinDistance: pinDistance
    });

    if (pinDistance <= 0) {
      log('Section ' + index + ': No pin distance, skipping');
      return;
    }

    // ============================================
    // MAIN STICKY PIN ANIMATION
    // ============================================
    ScrollTrigger.create({
      trigger: grid,
      start: function() {
        return 'top ' + currentHeaderOffset + 'px';
      },
      end: '+=' + pinDistance,
      pin: stickyContent,
      pinSpacing: true, // Add spacing to maintain section height
      scrub: true,
      markers: CONFIG.DEBUG,
      id: 'sticky-' + sectionId,
      invalidateOnRefresh: true,
      onEnter: function() {
        log('Section ' + index + ': Pin ACTIVATED');
      },
      onLeave: function() {
        log('Section ' + index + ': Pin DEACTIVATED (scrolled past)');
      },
      onEnterBack: function() {
        log('Section ' + index + ': Pin ACTIVATED (scroll back)');
      },
      onLeaveBack: function() {
        log('Section ' + index + ': Pin DEACTIVATED (scroll back above)');
      },
      onRefresh: function() {
        log('Section ' + index + ': ScrollTrigger refreshed with offset: ' + currentHeaderOffset);
      }
    });

    // ============================================
    // BUTTON FADE ANIMATION
    // ============================================
    const button = triggerButton || largeButton;

    if (button) {
      ScrollTrigger.create({
        trigger: grid,
        start: function() {
          return 'top ' + (currentHeaderOffset + 50) + 'px';
        },
        end: function() {
          return 'top ' + currentHeaderOffset + 'px';
        },
        scrub: true,
        id: 'button-fade-' + sectionId,
        invalidateOnRefresh: true,
        onUpdate: function(self) {
          // Fade out as button approaches header
          const opacity = 1 - self.progress;
          button.style.opacity = opacity;
          button.style.pointerEvents = opacity < 0.5 ? 'none' : 'auto';
        }
      });

      log('Section ' + index + ': Button fade animation created');
    }

    log('Section ' + index + ': Animations initialized');
  }

  // =============================================
  // DRAWER CONTROLS
  // =============================================

  function initDrawerControls() {
    log('Initializing drawer controls');

    // Drawer trigger buttons
    const drawerTriggers = document.querySelectorAll(CONFIG.SELECTORS.drawerTrigger);

    drawerTriggers.forEach(function(trigger) {
      const sectionId = trigger.dataset.drawerTrigger;
      const drawerOne = document.querySelector('[data-drawer-one="' + sectionId + '"]');
      const overlay = document.querySelector('[data-drawer-overlay="' + sectionId + '"]');

      if (!drawerOne || !overlay) return;

      trigger.addEventListener('click', function() {
        drawerOne.classList.add('is-open');
        overlay.classList.add('is-open');
        document.body.style.overflow = 'hidden';
        log('Drawer opened: ' + sectionId);
      });
    });

    // Drawer close buttons
    const drawerCloseButtons = document.querySelectorAll(CONFIG.SELECTORS.drawerClose);

    drawerCloseButtons.forEach(function(closeBtn) {
      const sectionId = closeBtn.dataset.drawerClose;
      const drawerOne = document.querySelector('[data-drawer-one="' + sectionId + '"]');
      const overlay = document.querySelector('[data-drawer-overlay="' + sectionId + '"]');

      if (!drawerOne || !overlay) return;

      closeBtn.addEventListener('click', function() {
        drawerOne.classList.remove('is-open');
        overlay.classList.remove('is-open');
        document.body.style.overflow = '';
        log('Drawer closed: ' + sectionId);
      });
    });

    // Overlay click to close
    const overlays = document.querySelectorAll(CONFIG.SELECTORS.drawerOverlay);

    overlays.forEach(function(overlay) {
      overlay.addEventListener('click', function() {
        const sectionId = overlay.dataset.drawerOverlay;
        const drawerOne = document.querySelector('[data-drawer-one="' + sectionId + '"]');

        if (drawerOne) {
          drawerOne.classList.remove('is-open');
        }
        overlay.classList.remove('is-open');
        document.body.style.overflow = '';
        log('Drawer closed via overlay: ' + sectionId);
      });
    });

    // Sub drawer trigger buttons
    const subDrawerTriggers = document.querySelectorAll(CONFIG.SELECTORS.subDrawerTrigger);

    subDrawerTriggers.forEach(function(trigger) {
      const sectionId = trigger.dataset.subDrawerTrigger;
      const drawerSub = document.querySelector('[data-drawer-sub="' + sectionId + '"]');

      if (!drawerSub) return;

      trigger.addEventListener('click', function() {
        drawerSub.classList.add('is-open');
        log('Sub-drawer opened: ' + sectionId);
      });
    });

    // Sub drawer close buttons
    const subDrawerCloseButtons = document.querySelectorAll(CONFIG.SELECTORS.subDrawerClose);

    subDrawerCloseButtons.forEach(function(closeBtn) {
      const sectionId = closeBtn.dataset.subDrawerClose;
      const drawerSub = document.querySelector('[data-drawer-sub="' + sectionId + '"]');
      const drawerOne = document.querySelector('[data-drawer-one="' + sectionId + '"]');
      const overlay = document.querySelector('[data-drawer-overlay="' + sectionId + '"]');

      if (!drawerSub || !drawerOne || !overlay) return;

      closeBtn.addEventListener('click', function() {
        drawerSub.classList.remove('is-open');

        // Wait for sub-drawer transition to complete (400ms) before closing drawer one
        setTimeout(function() {
          drawerOne.classList.remove('is-open');
          overlay.classList.remove('is-open');
          document.body.style.overflow = '';
          log('Sub-drawer closed, then drawer one: ' + sectionId);
        }, 400);
      });
    });

    log('Drawer controls initialized');
  }

  // =============================================
  // RESIZE HANDLER
  // =============================================

  function handleResize() {
    log('Window resized, refreshing ScrollTriggers');

    // Kill all existing ScrollTriggers
    ScrollTrigger.getAll().forEach(function(st) {
      st.kill();
    });

    // Reinitialize
    initScrollAnimations();
  }

  // =============================================
  // INITIALIZATION
  // =============================================

  function init() {
    log('=== HomeBlock03 GSAP Script Loaded ===');
    log('Breakpoint: ' + CONFIG.BREAKPOINT + 'px');
    log('Current width: ' + window.innerWidth + 'px');
    log('GSAP version: ' + (typeof gsap !== 'undefined' ? gsap.version : 'NOT LOADED'));
    log('ScrollTrigger available: ' + (typeof ScrollTrigger !== 'undefined'));

    // Check if GSAP and ScrollTrigger are loaded
    if (typeof gsap === 'undefined') {
      console.error('[HomeBlock03-GSAP] GSAP is not loaded! Make sure GSAP script is included before this file.');
      return;
    }

    if (typeof ScrollTrigger === 'undefined') {
      console.error('[HomeBlock03-GSAP] ScrollTrigger is not loaded! Make sure ScrollTrigger script is included.');
      return;
    }

    // Register ScrollTrigger plugin
    gsap.registerPlugin(ScrollTrigger);
    log('ScrollTrigger plugin registered');

    // Initialize scroll direction tracking (only once)
    initScrollDirectionTracking();

    // Initialize animations and controls
    initScrollAnimations();
    initDrawerControls();

    // Debounced resize handler
    let resizeTimer;
    window.addEventListener('resize', function() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(handleResize, 250);
    });

    log('Initialization complete');
  }

  // =============================================
  // INITIALIZATION TRACKING
  // =============================================

  let isInitialized = false;

  function ensureInitialized() {
    if (!isInitialized) {
      init();
      isInitialized = true;
    }
  }

  // =============================================
  // EXECUTE ON DOM READY
  // =============================================

  if (document.readyState === 'loading') {
    log('DOM loading, waiting for DOMContentLoaded...');
    document.addEventListener('DOMContentLoaded', ensureInitialized);
  } else {
    log('DOM already loaded, initializing immediately...');
    ensureInitialized();
  }

  // =============================================
  // SHOPIFY THEME EDITOR SUPPORT
  // =============================================

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    log('Shopify design mode detected');

    document.addEventListener('shopify:section:load', function(event) {
      log('Shopify section loaded, reinitializing...', event.detail);
      ensureInitialized(); // Make sure init ran at least once
      setTimeout(function() {
        ScrollTrigger.refresh();
        initScrollAnimations();
        initDrawerControls();
      }, 100);
    });

    document.addEventListener('shopify:section:unload', function(event) {
      log('Shopify section unloaded, cleaning up...', event.detail);
      ScrollTrigger.getAll().forEach(function(st) {
        st.kill();
      });
    });

    document.addEventListener('shopify:section:select', function(event) {
      log('Shopify section selected', event.detail);
      ScrollTrigger.refresh();
    });
  }

  // =============================================
  // PUBLIC API (for re-initialization if needed)
  // =============================================

  return {
    refresh: function() {
      ScrollTrigger.refresh();
    },
    reinit: function() {
      ScrollTrigger.getAll().forEach(function(st) { st.kill(); });
      initScrollAnimations();
      initDrawerControls();
    }
  };

})();
